import SeoHead from '../components/SeoHead'
import { Link } from 'react-router-dom'
import { Mail, LifeBuoy, ShieldAlert, Clock } from 'lucide-react'
import { SITE, mailto } from '../lib/site'

const CHANNELS = [
  {
    icon: LifeBuoy,
    title: 'Ürün ve teknik destek',
    body: 'Kurulum, entegrasyon, imza/doğrulama sorunları için.',
    addr: SITE.email.support,
    subject: 'W.Sign destek talebi',
  },
  {
    icon: Mail,
    title: 'Satış ve lisans',
    body: 'Kurumsal lisans, kendi altyapınıza kurulum, demo ve teklif için.',
    addr: SITE.email.sales,
    subject: 'W.Sign lisans / teklif',
  },
  {
    icon: ShieldAlert,
    title: 'Güvenlik bildirimi',
    body: 'Güvenlik açığı veya gizli bildirim için ayrı kanal.',
    addr: SITE.email.security,
    subject: 'W.Sign güvenlik bildirimi',
  },
]

export default function DestekPage() {
  return (
    <>
      <SeoHead
        title="Destek ve iletişim — W.Sign"
        description="W.Sign destek kanalları: teknik destek, satış ve güvenlik bildirimi. Kurumsal entegrasyon, kendi altyapınıza kurulum ve teklif talepleriniz için doğrudan bize yazın."
        path="/destek"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Belgelerim W.Sign sunucusunda mı saklanıyor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Portalda işlenen belgeler imza akışı için geçici olarak tutulur. W.Sign’ı kendi altyapınızda çalıştırdığınızda belge hiçbir zaman bizim sunucumuza gelmez.',
              },
            },
            {
              '@type': 'Question',
              name: 'W.Sign kullanım verisi / telemetri topluyor mu?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Hayır. W.Sign ürünleri kullanım telemetrisi toplamaz.',
              },
            },
            {
              '@type': 'Question',
              name: 'Kurulumda Windows uyarı veriyor, normal mi?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Kurulum paketinin kod imzalama sertifikası süreci tamamlanana kadar Windows SmartScreen uyarı gösterebilir. “Yine de çalıştır” ile kuruluma devam edebilirsiniz. Kurulum dosyası yalnızca sign.wsoft.tr üzerinden dağıtılır.',
              },
            },
            {
              '@type': 'Question',
              name: 'E-imzayı kendi uygulamamıza entegre edebilir miyiz?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Evet. Yönlendirmeli imza oturumu ve özet üzerinden imzalama API’si herkese açık geliştirici dokümanında anlatılır; kendi altyapınıza kurulum ve kapalı ağ senaryoları için satış kanalından yazabilirsiniz.',
              },
            },
          ],
        }}
      />

      <section className="container-page pt-16 pb-8 md:pt-20">
        <div className="max-w-2xl">
          <span className="badge">Destek</span>
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
            Yardıma mı ihtiyacınız var?
          </h1>
          <p className="mt-4 text-lg text-[var(--color-fg-muted)]">
            Doğru kanaldan yazın, size dönelim.
          </p>
        </div>
      </section>

      <section className="container-page pb-8">
        <div className="grid gap-4 md:grid-cols-3">
          {CHANNELS.map(c => (
            <a key={c.title} href={mailto(c.addr, c.subject)} className="card transition-colors hover:border-[var(--color-accent)]/40">
              <c.icon size={22} className="text-[var(--color-accent)]" />
              <h2 className="mt-3 font-semibold">{c.title}</h2>
              <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{c.body}</p>
              <p className="mt-3 font-mono text-sm text-[var(--color-accent)]">{c.addr}</p>
            </a>
          ))}
        </div>
        {SITE.phone && (
          <p className="mt-4 text-sm text-[var(--color-fg-muted)]">
            Telefon: <a href={`tel:${SITE.phone.replace(/\s/g, '')}`} className="font-medium text-[var(--color-fg)] hover:text-[var(--color-accent)]">{SITE.phone}</a>
          </p>
        )}
      </section>

      <section className="container-page py-8">
        <div className="flex items-center gap-2">
          <Clock size={20} className="text-[var(--color-fg-dim)]" />
          <h2 className="text-2xl font-semibold">Nasıl çalışıyoruz?</h2>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="card">
            <h3 className="font-semibold">Destek talepleri</h3>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              İş günlerinde dönüş yaparız. Sorunu hızlandırmak için hangi işlemde, hangi dosya
              tipiyle ve hangi kart/token ile karşılaştığınızı yazın.
            </p>
          </div>
          <div className="card">
            <h3 className="font-semibold">Kurumsal müşteriler</h3>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              Yanıt süresi ve erişilebilirlik taahhütleri kurumunuzla yapılan sözleşmede
              tanımlanır; ihtiyacınıza göre birlikte belirleriz.
            </p>
          </div>
          <div className="card">
            <h3 className="font-semibold">Güvenlik bildirimleri</h3>
            <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
              Ayrı kanaldan gelir ve önceliklidir. Bildirimi kamuya açmadan önce bize ulaşırsanız
              düzeltme sürecini birlikte yürütürüz.
            </p>
          </div>
        </div>
        <p className="mt-4 text-sm text-[var(--color-fg-muted)]">
          Kurumunuza uygun lisans ve teklif için yukarıdaki <strong>Satış ve lisans</strong> kanalından bize yazın.
        </p>
      </section>

      <section className="container-page py-12 pb-20">
        <h2 className="text-2xl font-semibold">Sık sorulanlar</h2>
        <div className="mt-6 space-y-4">
          <Faq q="Belgelerim sizin sunucunuzda mı saklanıyor?">
            sign.wsoft.tr portalında işlenen belgeler imza akışı için geçici olarak tutulur.
            W.Sign’ı kendi altyapınızda çalıştırdığınızda ise belge hiçbir zaman bizim
            sunucumuza gelmez. Ayrıntı için <Link to="/gizlilik" className="text-[var(--color-accent)] hover:underline">gizlilik</Link> sayfası.
          </Faq>
          <Faq q="Kullanım verisi / telemetri topluyor musunuz?">
            Hayır. W.Sign ürünleri kullanım telemetrisi toplamaz.
          </Faq>
          <Faq q="Kurulumda Windows uyarı veriyor, normal mi?">
            W.Sign kurulumu şu an dijital imza sertifikası sürecindedir; bu süreç
            tamamlanana kadar Windows SmartScreen bir uyarı gösterebilir. &ldquo;Yine de çalıştır&rdquo;
            ile kuruluma devam edebilirsiniz. Kurulum dosyası yalnızca {SITE.signUrl.replace('https://', '')} üzerinden dağıtılır.
          </Faq>
          <Faq q="E-imzayı kendi uygulamamıza entegre edebilir miyiz?">
            Evet. Entegrasyon yolları ve uçlar{' '}
            <Link to="/docs" className="text-[var(--color-accent)] hover:underline">geliştirici dokümanında</Link>{' '}
            açıkça anlatılıyor. Kendi altyapınıza kurulum veya kapalı ağ senaryoları için
            yukarıdaki <strong>Satış ve lisans</strong> kanalından bize yazın.
          </Faq>
        </div>
      </section>
    </>
  )
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="card group">
      <summary className="cursor-pointer list-none font-medium marker:hidden">
        <span className="flex items-center justify-between gap-2">
          {q}
          <span className="text-[var(--color-fg-dim)] transition-transform group-open:rotate-45">+</span>
        </span>
      </summary>
      <p className="mt-3 text-sm text-[var(--color-fg-muted)]">{children}</p>
    </details>
  )
}
